"use client";

import { FC, useEffect, useMemo, useState } from "react";
import * as Tabs from "@radix-ui/react-tabs";
import { useI18n } from "@/i18n/client";
import useStoreKeys from "@/hooks/useStoreKeys";
import mapProductOptions, {
	getProductOptions,
} from "@/hooks/mapProductOptions";
import { ProductData } from "@/lib/5874/types";
import { useCartContext } from "@/providers/CartContextProvider";
import { useProductContext } from "@/providers/ProductContextProvider";
import checkUserHasRolePermission from "@/hooks/checkUserHasRolePermission";
import ProductOption from "@/components/ProductCard/ChooseOptionsCTA/ProductOption/ProductOption";
import { ProductForm } from "../ProductForm/ProductForm";
import StockAvailability from "@/components/StockAvailability/StockAvailability";
import SelectInput from "@/components/UI/Input/SelectInput/SelectInput";
import Notification from "@/components/UI/Notification/Notification";
import styles from "./ProductComplexForm.module.scss";
import clsx from "clsx";
import ProductTotalPrice from "@/components/PDP/ProductTotalPrice/ProductTotalPrice";
import { getLineItemForProduct } from "@/utils/cart-utils";
import AddToWishlistCTAComplex from "@/components/UI/AddToWishlistCTA/AddToWishlistCTAComplex";
import { useSessionContext } from "@/lib/auth/SessionContextProvider";

type Props = {
	showAddToCart: boolean;
};

const ProductComplexForm: FC<Props> = ({ showAddToCart }) => {
	const { store, lang } = useStoreKeys();
	const { product: parent } = useProductContext();
	const { session } = useSessionContext();
	const { cart } = useCartContext();
	const [showRemoveMessage, setShowRemoveMessage] = useState(false);
	const t = useI18n();

	const productOptions = useMemo(() => {
		let options: ProductData[] = [];
		if (parent.children) options = parent.children;
		if (parent.siblings) options = parent.siblings;
		// Remove null products
		options = options
			.filter((option) => option)
			.sort((a, b) => (a.name > b.name ? 1 : -1));

		return [...new Set(options)];
	}, [parent]);

	const mappedOptions = useMemo(() => {
		return mapProductOptions(productOptions);
	}, [productOptions]);

	const [currentProductOptions] = getProductOptions([parent]);
	const productOptionsWithVariants = getProductOptions(productOptions);

	const [selectedOptions, setSelectedOptions] = useState<
		Record<string, string>
	>(currentProductOptions?.variantOptions || {});

	useEffect(() => {
		if (
			Object.keys(selectedOptions).length === Object.keys(mappedOptions).length
		) {
			const product = productOptionsWithVariants.find((prod) =>
				Object.entries(selectedOptions).every(
					([optionField, optionValue]) =>
						prod.variantOptions[optionField] === optionValue,
				),
			);
			if (product && parent.path !== product?.productPath) {
				window.location.href = `/${store}/${lang}/${product?.productPath}`;
			}
		}
	}, [selectedOptions]);

	if (!checkUserHasRolePermission("purchase_enable")) return <></>;

	const handleRemovedFromCart = () => {
		setShowRemoveMessage(true);
		setTimeout(() => setShowRemoveMessage(false), 3000);
	};

	const productOptionsTotal = useMemo(() => {
		let total = 0;

		productOptions.forEach((product) => {
			let lineItem = getLineItemForProduct(cart, product);

			if (lineItem) {
				total = total + product.pricesExTax.price.value * lineItem.quantity;
			}
		});

		return total;
	}, [productOptions, cart]);

	return (
		<>
			<AddToWishlistCTAComplex userId={session?.userId} />
			<Tabs.Root defaultValue='add-single' className={styles.tabs}>
				<Tabs.List className={styles.tabList}>
					<Tabs.Trigger value='add-single'>
						{t("product.addSingle")}
					</Tabs.Trigger>
					<Tabs.Trigger value='add-multiple'>
						{t("product.addMultiple")}
					</Tabs.Trigger>
				</Tabs.List>
				<Tabs.Content className={styles.tabContent} value='add-single'>
					<div className={styles.selectOptions}>
						{mappedOptions.map((customField, index) => {
							const previousLabels = mappedOptions
								.filter((_, i) => i < index)
								.map((o) => o.label);
							const nextLabels = mappedOptions
								.filter((_, i) => i > index)
								.map((o) => o.label);

							const options = customField.options.map((option) => {
								const isAvailable = productOptionsWithVariants.filter(
									(prod) => {
										if (!previousLabels.length) return true;
										// Check if there is any product available for the current option.
										return previousLabels.every(
											(opt) =>
												prod.variantOptions[opt] === selectedOptions[opt] &&
												prod.variantOptions[customField.label] === option.label,
										);
									},
								);

								return {
									text: option.label,
									value: option.label,
									disabled: !isAvailable.length,
								};
							});

							return (
								<div key={customField.label} className={styles.optionField}>
									<label>{customField.label}</label>
									<SelectInput
										label={customField.label}
										placeHolder={t("product.pleaseSelect")}
										options={options}
										onChange={(value: string) => {
											let copyOfSelectedOptions = {
												...selectedOptions,
											};
											// For example, if the product has multiple options to select and we modified 1 option, then we delete all the values that were selected for the subsequent options.
											nextLabels.forEach(
												(label) => delete copyOfSelectedOptions[label],
											);
											return setSelectedOptions({
												...copyOfSelectedOptions,
												[customField.label]: value,
											});
										}}
										value={selectedOptions[customField.label]}
										disabled={!productOptions}
									/>
								</div>
							);
						})}
					</div>
					{parent.productClass !== "Parent" && (
						<ProductForm showAddToCart={showAddToCart} />
					)}
					<StockAvailability
						product={parent}
						className={styles.stockAvailability}
					/>
					<ProductTotalPrice />
				</Tabs.Content>
				<Tabs.Content
					className={clsx([styles.tabContent, styles.wide])}
					value='add-multiple'
				>
					<div className={styles.optionsGrid}>
						{productOptions?.map((product: ProductData) => (
							<ProductOption
								key={String(product?.id)}
								product={product}
								handleRemovedFromCart={handleRemovedFromCart}
							/>
						))}
					</div>
					<ProductTotalPrice price={Number(productOptionsTotal)} />
				</Tabs.Content>
			</Tabs.Root>
			{showRemoveMessage && (
				<Notification
					type='toast'
					variant='success'
					title={t("cart.removedFromCart")}
				/>
			)}
		</>
	);
};

export default ProductComplexForm;
